import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Mail, Download, Search, Loader2, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const PAGE_SIZE = 20;

export const SubscribersCard = () => {
  const [preview, setPreview] = useState(null); // { subscribers, total, total_all }
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.getSubscribers(0, 5, '').then(setPreview).catch(() => setPreview({ subscribers: [], total: 0, total_all: 0 }));
  }, []);

  const exportCSV = async () => {
    if (!preview?.total_all) {
      toast.error('No subscribers yet');
      return;
    }
    setExporting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${backendUrl}/api/subscribers/export`, { credentials: 'include' });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `linkmonkey-subscribers.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${preview.total_all} subscriber(s)`);
    } catch (e) {
      toast.error(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (!preview) {
    return (
      <Card className="p-6 mt-6" data-testid="subscribers-card">
        <p className="text-sm text-muted-foreground">Loading subscribers...</p>
      </Card>
    );
  }

  return (
    <>
      <Card className="p-6 mt-6" data-testid="subscribers-card">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Mail className="w-6 h-6 text-primary" />
            <h3 className="text-xl font-semibold">Email Subscribers</h3>
            {preview.total_all > 0 && (
              <Badge variant="secondary" data-testid="subscribers-count">{preview.total_all}</Badge>
            )}
          </div>
          {preview.total_all > 0 && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="view-all-subs-btn">
                <Search className="w-4 h-4 mr-1.5" /> Manage
              </Button>
              <Button size="sm" variant="outline" onClick={exportCSV} disabled={exporting} data-testid="export-subs-btn">
                {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
                Export CSV
              </Button>
            </div>
          )}
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Emails collected from your profile's signup widget. Subscribers appear here once they click the confirmation link we email them.
        </p>
        {preview.pending > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm" data-testid="pending-subs-note">
            <strong>{preview.pending}</strong> signup{preview.pending === 1 ? '' : 's'} waiting for email confirmation — they'll show up here as soon as they confirm.
          </div>
        )}

        {preview.total_all === 0 ? (
          <div className="p-6 text-center rounded-xl bg-muted/30 border border-dashed border-border">
            <Mail className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No confirmed subscribers yet.</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Visitors who sign up get a confirmation email first — they appear here once they click it.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-1.5" data-testid="subscribers-preview-list">
              {preview.subscribers.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/20 text-sm">
                  <span className="font-medium truncate" data-testid={`sub-email-${i}`}>{s.email}</span>
                  <span className="text-xs text-muted-foreground flex-shrink-0 ml-3">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}
                  </span>
                </div>
              ))}
            </div>
            {preview.total_all > 5 && (
              <Button variant="ghost" size="sm" className="w-full mt-2"
                onClick={() => setOpen(true)} data-testid="open-subs-dialog">
                View all {preview.total_all} subscribers →
              </Button>
            )}
          </>
        )}
      </Card>

      <SubscribersDialog open={open} onClose={() => setOpen(false)} onExport={exportCSV} exporting={exporting} />
    </>
  );
};

const SubscribersDialog = ({ open, onClose, onExport, exporting }) => {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSearch('');
      setPage(0);
    }
  }, [open]);

  // Debounced fetch on search/page changes
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const handle = setTimeout(() => {
      api.getSubscribers(page * PAGE_SIZE, PAGE_SIZE, search.trim())
        .then(setData)
        .catch(() => setData({ subscribers: [], total: 0, total_all: 0 }))
        .finally(() => setLoading(false));
    }, search ? 250 : 0);
    return () => clearTimeout(handle);
  }, [open, page, search]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="subscribers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Email Subscribers
            {data?.total_all > 0 && (
              <Badge variant="secondary">{data.total_all} total</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Search + export */}
        <div className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="Search by email..."
              className="pl-9"
              data-testid="subs-search"
            />
            {search && (
              <button onClick={() => { setSearch(''); setPage(0); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted">
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={onExport} disabled={exporting} data-testid="dialog-export-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            Export all
          </Button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto -mx-1 px-1 min-h-[200px]">
          {loading && !data ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : data?.subscribers?.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">
              {search ? `No subscribers matching "${search}"` : 'No subscribers yet.'}
            </p>
          ) : (
            <div className="space-y-1" data-testid="subs-full-list">
              {data?.subscribers?.map((s, i) => (
                <div key={`${s.email}-${i}`} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/20 text-sm hover:bg-muted/30 transition-colors">
                  <span className="font-medium truncate">{s.email}</span>
                  <span className="text-xs text-muted-foreground flex-shrink-0 ml-3">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {data && data.total > PAGE_SIZE && (
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <p className="text-xs text-muted-foreground">
              {data.skip + 1}–{Math.min(data.skip + data.subscribers.length, data.total)} of {data.total}
            </p>
            <div className="flex items-center gap-1">
              <Button size="icon" variant="outline" className="h-8 w-8" disabled={page === 0 || loading}
                onClick={() => setPage(p => Math.max(0, p - 1))} data-testid="subs-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs px-2 tabular-nums">{page + 1} / {totalPages}</span>
              <Button size="icon" variant="outline" className="h-8 w-8" disabled={page >= totalPages - 1 || loading}
                onClick={() => setPage(p => p + 1)} data-testid="subs-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubscribersCard;
